import { useState } from "react";
import { Plus, Edit, Trash2, Search, Fuel } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { type TamanhoPagina } from "@/hooks/usePaginacaoLocal";
import { useDebounce } from "@/hooks/useDebounce";
import { AnpSearchCombobox } from "@/components/AnpSearchCombobox";
import { useProductUnits } from "@/features/produtos/hooks/useProductUnits";
import {
  useMonofasicoAnp,
  type MonofasicoAnpListagem,
  type SalvarMonofasicoAnpRequest,
} from "@/features/fiscal/hooks/useMonofasicoAnp";
import { toast } from "sonner";

const EMPTY: SalvarMonofasicoAnpRequest = {
  anpId: 0,
  codigoAnp: "",
  descricaoAnp: "",
  bio: false,
  origemNacional: false,
  adRemIcms: 0,
  unidadeMedidaId: null,
  descricaoMedida: "",
  observacoes: "",
};

export default function MonofasicosAnp() {
  const [buscaInput, setBuscaInput] = useState("");
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState<TamanhoPagina>(20);
  const busca = useDebounce(buscaInput, 400);

  const { query, obterPorId, criar, atualizar, excluir } = useMonofasicoAnp(busca, pagina, tamanhoPagina);
  const { units } = useProductUnits();

  const itens = query.data?.itens ?? [];
  const total = query.data?.total ?? 0;
  const totalPaginas = query.data?.totalPaginas ?? 1;
  const primeiroItem = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const ultimoItem = Math.min(pagina * tamanhoPagina, total);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [form, setForm] = useState<SalvarMonofasicoAnpRequest>(EMPTY);

  const set = (campo: keyof SalvarMonofasicoAnpRequest, valor: unknown) =>
    setForm(f => ({ ...f, [campo]: valor }));

  const openNew = () => { setEditId(null); setForm(EMPTY); setDialogOpen(true); };

  const openEdit = async (id: number) => {
    try {
      const dto = await obterPorId(id);
      setEditId(id);
      setForm({
        anpId: dto.anpId,
        codigoAnp: dto.codigoAnp ?? "",
        descricaoAnp: dto.descricaoAnp ?? "",
        bio: dto.bio,
        origemNacional: dto.origemNacional,
        adRemIcms: dto.adRemIcms,
        unidadeMedidaId: dto.unidadeMedidaId ?? null,
        descricaoMedida: dto.descricaoMedida ?? "",
        observacoes: dto.observacoes ?? "",
      });
      setDialogOpen(true);
    } catch {
      toast.error("Erro ao carregar monofásico ANP.");
    }
  };

  const handleSubmit = () => {
    if (!form.anpId) { toast.error("Selecione a classificação ANP."); return; }
    if (editId) atualizar.mutate({ id: editId, req: form }, { onSuccess: () => setDialogOpen(false) });
    else criar.mutate(form, { onSuccess: () => setDialogOpen(false) });
  };

  const handleDelete = () => {
    if (deleteId) excluir.mutate(deleteId, { onSuccess: () => setDeleteId(null) });
  };

  const unidadeLabel = (u: { sigla?: string; descricao?: string }) =>
    [u.sigla, u.descricao].filter(Boolean).join(" — ") || "—";

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Fuel className="h-6 w-6" /> Monofásicos da ANP
          </h1>
          <p className="text-muted-foreground">Combustíveis — relaciona a classificação ANP aos parâmetros fiscais (ICMS ad rem, unidade tributável).</p>
        </div>
        <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Novo Monofásico</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por código ou classificação ANP..."
          className="pl-9"
          value={buscaInput}
          onChange={e => { setBuscaInput(e.target.value); setPagina(1); }}
        />
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Código ANP</TableHead>
              <TableHead>Classificação</TableHead>
              <TableHead className="text-right w-[120px]">adRem ICMS</TableHead>
              <TableHead className="text-center w-[110px]">Origem Nac.</TableHead>
              <TableHead className="w-24">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : itens.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nenhum monofásico ANP cadastrado.</TableCell></TableRow>
            ) : (
              itens.map((m: MonofasicoAnpListagem) => (
                <TableRow key={m.id}>
                  <TableCell className="font-mono font-semibold">{m.codigoAnp ?? "—"}</TableCell>
                  <TableCell className="text-sm">{m.descricaoAnp ?? "—"}</TableCell>
                  <TableCell className="text-right font-mono">{m.adRemIcms?.toFixed(4) ?? "0,0000"}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant={m.origemNacional ? "default" : "secondary"}>{m.origemNacional ? "Sim" : "Não"}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(m.id)}><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {query.data && (
        <PaginacaoRodape
          pagina={pagina}
          totalPaginas={totalPaginas}
          totalItens={total}
          primeiroItem={primeiroItem}
          ultimoItem={ultimoItem}
          tamanhoPagina={tamanhoPagina}
          onPaginaChange={setPagina}
          onTamanhoPaginaChange={(t) => { setTamanhoPagina(t); setPagina(1); }}
        />
      )}

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editId ? "Editar Monofásico ANP" : "Novo Monofásico ANP"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <Label>Classificação ANP <span className="text-destructive">*</span></Label>
              <AnpSearchCombobox
                value={form.anpId || undefined}
                displayLabel={form.codigoAnp ? `${form.codigoAnp} — ${form.descricaoAnp}` : undefined}
                onChange={(anpId, codigo, descricao) => {
                  setForm(f => ({ ...f, anpId, codigoAnp: codigo, descricaoAnp: descricao }));
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>adRem ICMS (R$ por unidade)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={form.adRemIcms ?? 0}
                  onChange={e => set("adRemIcms", e.target.value === "" ? 0 : Number(e.target.value))}
                  placeholder="0,0000"
                />
              </div>
              <div className="space-y-1">
                <Label>Unidade de Medida Tributável</Label>
                <Select
                  value={form.unidadeMedidaId ? String(form.unidadeMedidaId) : ""}
                  onValueChange={(v) => {
                    const u = (units ?? []).find(x => x.id === Number(v));
                    setForm(f => ({ ...f, unidadeMedidaId: Number(v), descricaoMedida: u ? unidadeLabel(u) : "" }));
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>
                    {(units ?? []).filter(u => u.isAtivo).map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{unidadeLabel(u)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center space-x-2">
                <Checkbox id="bio" checked={form.bio} onCheckedChange={v => set("bio", !!v)} />
                <label htmlFor="bio" className="text-sm cursor-pointer">Percentual bio combustível</label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox id="origemNacional" checked={form.origemNacional} onCheckedChange={v => set("origemNacional", !!v)} />
                <label htmlFor="origemNacional" className="text-sm cursor-pointer">Origem nacional</label>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Observações</Label>
              <Textarea value={form.observacoes ?? ""} maxLength={500} rows={2} onChange={e => set("observacoes", e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} disabled={!form.anpId || criar.isPending || atualizar.isPending}>
              {criar.isPending || atualizar.isPending ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog excluir */}
      <AlertDialog open={deleteId !== null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir monofásico ANP?</AlertDialogTitle>
            <AlertDialogDescription>Esta ação não pode ser desfeita.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
